import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "tenant-admin-theme";

/** @typedef {"light" | "dark" | "system"} ThemePreference */

function readStored() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  // Default to light rather than following the OS, so the product looks the
  // same as sentrodesk.com for a first-time visitor.
  return "light";
}

/** @param {ThemePreference} pref */
function resolveTheme(pref) {
  if (pref === "dark") return "dark";
  if (pref === "light") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Canvas fill — set on <html> so it wins over Tailwind layer order and survives theme switches. */
export const APP_CANVAS_LIGHT = "#ffffff";
export const APP_CANVAS_DARK = "#020617";

function applyDocumentTheme(resolved) {
  const root = document.documentElement;
  const isDark = resolved === "dark";
  // `.dark` class drives Tailwind `dark:` (darkMode:"class").
  // `data-theme` drives plain-CSS hooks (html[data-theme="dark"]).
  // Always set both together so they can never drift.
  if (isDark) {
    root.classList.add("dark");
    root.dataset.theme = "dark";
  } else {
    root.classList.remove("dark");
    root.dataset.theme = "light";
  }
  // Prevent a `.dark` class on body (some libs add it) from creating false positives.
  document.body.classList.remove("dark");
  root.style.colorScheme = isDark ? "dark" : "light";
  root.style.setProperty("--app-canvas", isDark ? APP_CANVAS_DARK : APP_CANVAS_LIGHT);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", isDark ? APP_CANVAS_DARK : APP_CANVAS_LIGHT);
  }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStored());
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    typeof window !== "undefined" ? resolveTheme(readStored()) : "light",
  );

  const setTheme = useCallback((next) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    // Apply immediately (don’t wait for layout effect) so Save → light updates the DOM in the same task
    const resolved = resolveTheme(next);
    applyDocumentTheme(resolved);
    setResolvedTheme(resolved);
    setThemeState(next);
  }, []);

  // Run before paint so theme class + data-theme never flash wrong after save or navigation
  useLayoutEffect(() => {
    const r = resolveTheme(theme);
    setResolvedTheme(r);
    applyDocumentTheme(r);
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onOsThemeChange = () => {
      const r = resolveTheme("system");
      setResolvedTheme(r);
      applyDocumentTheme(r);
    };
    mq.addEventListener("change", onOsThemeChange);
    return () => mq.removeEventListener("change", onOsThemeChange);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme, resolvedTheme }), [theme, setTheme, resolvedTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook and provider share one context module
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
